import React, { FunctionComponent, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
    ContentSection,
    GridRow,
    GridColumn,
    ListTable,
    ButtonRenderProps,
    ButtonProps,
    ActionButton,
    Checkbox,
    DetailsRowProps,
    Grid,
    Stack,
    RenderFunction,
    DropdownMultiselect,
    depascalize
} from "sprint-common-web";

import {
    CreateContentVersionButton,
    CreateVersionButtonProps
} from "../../../../common/components/content-version/CreateContentVersionButton";
import { TagElementsList } from "../../../../common/components/TagElementsList";
import { ContentReferenceType } from "../../../../common/types/ContentReferenceType";
import { ContentVersionSource } from "../../../common/types/ContentVersionSource";
import { getSlicedText } from "../../../common/utils/getSlicedText";
import { renderNotMergableRow } from "../../../common/utils/renderNotMergableRow";
import { validateVersionsMergeability } from "../../../common/utils/validateVersionsMergeability";
import { MovieVersionDetails } from "../MovieDetails";
import { MergeMovieVersionsCommandBarButton } from "./buttons/MergeMovieVersionsCommandBarButton";
import { MovieVersionListingContext } from "./context/MovieVersionListingContext";
import { createMovieVersionListingColumns } from "./createMovieVersionListingColumns";
import { movieVersionOrigins } from "./MovieVersionListingFilters";
import { MovieVersionListingItem } from "./MovieVersionListingItem";

export const MovieVersionListing: FunctionComponent = () => {
    const {
        listingItems,
        filters,
        isLoading,
        totalItems,
        movieDetails,
        versionsToMerge,
        setSort,
        setPage,
        setPageSize,
        setSources,
        setIncludeMerged,
        clearFilters,
        addVersionToMergeList,
        removeVersionFromMergeList,
        addNewVersionToMovie
    } = useContext(MovieVersionListingContext);
    const history = useHistory();

    const goToPath = (path: string) => {
        history.push(path);
    };

    const columns = useMemo(
        () =>
            createMovieVersionListingColumns({
                movieDetails,
                addVersionToMergeList,
                versionsToMerge,
                goToPath
            }),
        [movieDetails, addVersionToMergeList, versionsToMerge, goToPath]
    );

    const handleMergedCheckboxChange = (
        _ev?: React.FormEvent<HTMLElement | HTMLInputElement> | undefined,
        checked?: boolean | undefined
    ) => {
        setIncludeMerged(checked);
    };

    const renderRow = (rowProps?: DetailsRowProps, renderDefault?: RenderFunction<DetailsRowProps>) => {
        if (isRowMergable(movieDetails?.versions ?? [], versionsToMerge, rowProps?.item) && renderDefault) {
            return renderDefault(rowProps, renderDefault);
        }

        return renderNotMergableRow(rowProps, renderDefault);
    };

    const originOptions = movieVersionOrigins.map((origin) => ({
        key: origin,
        text: depascalize(ContentVersionSource[origin])
    }));

    const createNewVersionButtonProps: ButtonRenderProps<CreateVersionButtonProps> = {
        render: CreateContentVersionButton,
        content: {
            type: ContentReferenceType.Movie,
            movieId: movieDetails.id
        },
        onContentVersionCreated: (newVersion) => {
            addNewVersionToMovie(newVersion);
        },
        isLabeled: true
    };

    const mergeVersionsButtonProps: ButtonRenderProps<ButtonProps> = {
        render: MergeMovieVersionsCommandBarButton
    };

    return (
        <>
            <ContentSection header="Versions" buttons={[mergeVersionsButtonProps, createNewVersionButtonProps]}>
                <Grid>
                    <GridRow style={{ margin: 0 }}>
                        <TagElementsList
                            items={versionsToMerge}
                            onItemDismiss={removeVersionFromMergeList}
                            renderItemText={getMergeMovieVersionText}
                            styles={{ root: { paddingBottom: 10 } }}
                        />
                    </GridRow>
                    <GridRow style={{ margin: 0 }}>
                        <GridColumn sm={3}>
                            <DropdownMultiselect
                                placeholder="Version Source"
                                selectedKeys={filters.sources}
                                onChange={setSources}
                                options={originOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack styles={{ root: { paddingTop: 5 } }}>
                                <Checkbox
                                    label="Include Merge Sources"
                                    onChange={handleMergedCheckboxChange}
                                    checked={filters.includeMerged ?? false}
                                />
                            </Stack>
                        </GridColumn>
                        <GridColumn sm={8}>
                            <Stack horizontalAlign="end">
                                <ActionButton
                                    iconProps={{ iconName: "ClearFilter" }}
                                    ariaLabel="Clear filters"
                                    label="Clear filters"
                                    onClick={clearFilters}
                                >
                                    Clear filters
                                </ActionButton>
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                items={listingItems}
                                pagination={{
                                    ...filters,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSortChange={setSort}
                                isLoading={isLoading}
                                onRenderRow={versionsToMerge.length ? renderRow : undefined}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const isRowMergable = (
    versionsDetails: MovieVersionDetails[],
    versionsToMerge: MovieVersionListingItem[],
    rowItem?: MovieVersionListingItem
) => {
    const versionToMerge = versionsToMerge[0];

    const versionsDetailsToMerge =
        versionsDetails.filter(
            (version: MovieVersionDetails) => version.id === versionToMerge?.id || version.id === rowItem?.id
        ) ?? [];

    const areMergeable = validateVersionsMergeability(versionsDetailsToMerge);

    return (
        (areMergeable && versionsToMerge.length < 2) ||
        versionsToMerge.some((version: MovieVersionListingItem) => version.id === rowItem?.id)
    );
};

export const getMergeMovieVersionText = (item: MovieVersionListingItem, sliceBuzzVersion = true): string => {
    const { distributorName, brandings, buzzVersion, typeName } = item;
    const { name = "" } = buzzVersion || {};
    return `${distributorName} - ${typeName}${brandings.length ? ` - ${brandings}` : ""}${
        !!name ? ` - ${sliceBuzzVersion ? getSlicedText(name, 0, 40, "...") : name}` : ""
    }`;
};
